import React, { useEffect, useMemo, useRef, useState } from "react";
import { Megaphone, ImagePlus, Send, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api, mediaSrcById } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";

const ROLES = ["installer", "supervisor", "office", "manager", "admin"];

const readAsBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result).split(",")[1]);
  r.onerror = rej;
  r.readAsDataURL(file);
});

/** MASTER: send a flyer/image notification to a chosen audience, N times. */
export const BroadcastPanel = ({ t, accounts = [] }) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [mediaId, setMediaId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState("all");
  const [companyId, setCompanyId] = useState("");
  const [role, setRole] = useState("installer");
  const [userIds, setUserIds] = useState([]);
  const [userQ, setUserQ] = useState("");
  const [times, setTimes] = useState(1);
  const [interval, setInterval_] = useState(24);
  const [preview, setPreview] = useState(null);
  const [schedule, setSchedule] = useState(false);
  const [firstAt, setFirstAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const fileRef = useRef(null);

  const companies = useMemo(() => {
    const m = new Map();
    accounts.forEach((a) => { if (a.company_id && a.company_name) m.set(a.company_id, a.company_name); });
    return [...m.entries()];
  }, [accounts]);

  const audience = useMemo(() => ({
    mode, company_id: companyId || null, role: mode === "role" || mode === "company_role" ? role : null, user_ids: userIds,
  }), [mode, companyId, role, userIds]);

  const load = () => api.get("/admin/broadcasts").then((r) => setList(r.data?.broadcasts || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.post("/admin/broadcasts/preview", audience).then((r) => setPreview(r.data)).catch(() => setPreview(null));
  }, [audience]);

  const pickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const b64 = await readAsBase64(f);
      const { data } = await api.post("/media", { data_base64: b64, content_type: f.type || "image/jpeg", kind: "image" });
      setMediaId(data.id);
    } catch (err) { toast.error(t("something_wrong")); } finally { setUploading(false); }
  };

  const send = async () => {
    if (!title.trim() && !mediaId) { toast.error(t("bc_need_title_or_image")); return; }
    if (schedule && !firstAt) { toast.error(t("bc_need_datetime")); return; }
    setBusy(true);
    try {
      await api.post("/admin/broadcasts", {
        title, body, media_id: mediaId, audience, times: Number(times) || 1, interval_hours: Number(interval) || 24,
        send_now: !schedule, first_send_at: schedule ? firstAt : null, // NY local time
      });
      toast.success(schedule ? t("bc_scheduled") : t("bc_sent"));
      setTitle(""); setBody(""); setMediaId(null); setUserIds([]); setFirstAt("");
      load();
    } catch (err) { toast.error(t("something_wrong")); } finally { setBusy(false); }
  };

  const stop = async (id) => {
    try { await api.delete(`/admin/broadcasts/${id}`); load(); } catch (err) { toast.error(t("something_wrong")); }
  };

  const filteredUsers = accounts.filter((a) => !userQ || `${a.name} ${a.email} ${a.company_name}`.toLowerCase().includes(userQ.toLowerCase())).slice(0, 12);
  const modeLabel = (m) => t(`bc_mode_${m}`);

  return (
    <Card className="p-4 space-y-3" data-testid="broadcast-panel">
      <div className="flex items-center gap-2 font-semibold"><Megaphone className="h-4 w-4 text-primary" /> {t("bc_title")}</div>
      <p className="text-xs text-muted-foreground">{t("bc_hint")}</p>

      <Input data-testid="bc-title" placeholder={t("bc_title_ph")} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
      <Input data-testid="bc-body" placeholder={t("bc_body_ph")} value={body} onChange={(e) => setBody(e.target.value)} maxLength={400} />

      <div className="flex items-center gap-3">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="bc-file" />
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="bc-pick-image">
          <ImagePlus className="h-4 w-4 mr-1.5" /> {uploading ? "…" : t("bc_pick_image")}
        </Button>
        {mediaId && (
          <div className="relative">
            <img src={mediaSrcById(mediaId)} alt="" className="h-16 w-12 object-cover rounded border" data-testid="bc-image-preview" />
            <button type="button" onClick={() => setMediaId(null)} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-destructive text-white flex items-center justify-center"><X className="h-3 w-3" /></button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger data-testid="bc-mode"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "company", "company_role", "role", "solo", "individual"].map((m) => <SelectItem key={m} value={m}>{modeLabel(m)}</SelectItem>)}
          </SelectContent>
        </Select>
        {(mode === "company" || mode === "company_role") && (
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger data-testid="bc-company"><SelectValue placeholder={t("bc_pick_company")} /></SelectTrigger>
            <SelectContent>{companies.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {(mode === "role" || mode === "company_role") && (
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="bc-role"><SelectValue /></SelectTrigger>
            <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{t(r)}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {mode === "individual" && (
        <div className="space-y-1.5">
          <Input placeholder={t("search")} value={userQ} onChange={(e) => setUserQ(e.target.value)} data-testid="bc-user-search" />
          <div className="flex flex-wrap gap-1">
            {filteredUsers.map((a) => {
              const on = userIds.includes(a.id);
              return (
                <button key={a.id} type="button" data-testid={`bc-user-${a.id}`}
                  onClick={() => setUserIds((s) => on ? s.filter((x) => x !== a.id) : [...s, a.id])}
                  className={`text-xs px-2 py-1 rounded-full border ${on ? "bg-primary text-primary-foreground" : "bg-background"}`}>
                  {a.name}{a.company_name ? ` · ${a.company_name}` : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-muted-foreground">{t("bc_times")}
          <Input type="number" min={1} max={30} value={times} onChange={(e) => setTimes(e.target.value)} data-testid="bc-times" />
        </label>
        <label className="text-xs text-muted-foreground">{t("bc_interval")}
          <Input type="number" min={0.25} step={0.25} value={interval} onChange={(e) => setInterval_(e.target.value)} disabled={Number(times) <= 1} data-testid="bc-interval" />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border overflow-hidden text-xs">
          <button type="button" onClick={() => setSchedule(false)} data-testid="bc-when-now"
            className={`px-3 py-1.5 ${!schedule ? "bg-primary text-primary-foreground" : "bg-background"}`}>{t("bc_when_now")}</button>
          <button type="button" onClick={() => setSchedule(true)} data-testid="bc-when-schedule"
            className={`px-3 py-1.5 ${schedule ? "bg-primary text-primary-foreground" : "bg-background"}`}>{t("bc_when_schedule")}</button>
        </div>
        {schedule && (
          <input type="datetime-local" value={firstAt} onChange={(e) => setFirstAt(e.target.value)} data-testid="bc-first-at"
            className="h-9 rounded-md border bg-background px-2 text-sm" />
        )}
        {schedule && <span className="text-[11px] text-muted-foreground">{t("bc_ny_time")}</span>}
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-muted-foreground flex items-center gap-1" data-testid="bc-preview">
          <Users className="h-3.5 w-3.5" /> {preview ? `${preview.count} ${t("bc_recipients")}` : "…"}
        </div>
        <Button onClick={send} disabled={busy || uploading} data-testid="bc-send"><Send className="h-4 w-4 mr-1.5" /> {schedule ? t("bc_schedule_btn") : t("bc_send_now")}</Button>
      </div>

      {list.length > 0 && (
        <div className="space-y-1.5 pt-2 border-t">
          <div className="text-xs font-semibold text-muted-foreground">{t("bc_history")}</div>
          {list.map((b) => (
            <div key={b.id} className="flex items-center gap-2 text-xs rounded border px-2 py-1.5" data-testid="bc-row">
              {b.media_id && <img src={mediaSrcById(b.media_id)} alt="" className="h-9 w-7 object-cover rounded" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{b.title || "—"}</div>
                <div className="text-muted-foreground truncate">{modeLabel(b.audience?.mode || "all")} · {b.sends}/{b.times} · 👁 {b.seen_count}</div>
                {b.status === "active" && b.next_send_at && (
                  <div className="text-[11px] text-primary" data-testid="bc-next">
                    {b.sends === 0 ? t("bc_scheduled_for") : t("bc_next_send")} {new Date(b.next_send_at).toLocaleString(undefined, { timeZone: "America/New_York", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </div>
                )}
              </div>
              <Badge variant={b.status === "active" ? "default" : "secondary"} className="text-[10px]">{b.status}</Badge>
              {b.status === "active" && (
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => stop(b.id)} data-testid="bc-stop"><Trash2 className="h-3.5 w-3.5" /></Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
